import type {
  Frame,
  Point,
  Projector,
  RenderFn,
  RenderOutput,
} from "./pointField";
import {
  CUT_SCAT,
  EDGE_K,
  GLOW_K,
  SCAT,
  TWK,
  TWK_HZ,
  frontStates,
  lightAt,
} from "./tuning";

export interface ExitFrame {
  /** Collapse progress, 0…1. */
  u: number;
  collapse: number;
}

export interface HomeFieldOptions {
  /** Called every frame while collapsing, so the caller can drive the hero and wash. */
  onExitProgress(state: ExitFrame): void;
  /** Called once, after the hold, when it is time to navigate. */
  onExitComplete(): void;
  /** Skip both animations and navigate immediately. */
  reducedMotion?: boolean;
  /** Returns current mouse position in canvas-space, or null when outside. */
  getMouse?(): { x: number; y: number } | null;
}

export interface HomeField {
  render: RenderFn;
  /** Begin the collapse. Idempotent while one is running. */
  launch(): void;
  running(): boolean;
}

/** Smooth the mouse attractor position with a spring so it never teleports. */
interface MouseSpring {
  x: number;
  y: number;
  vx: number;
  vy: number;
  active: boolean;
  strength: number;
  lastSpeedScale: number;
}

export function createHomeField(opts: HomeFieldOptions): HomeField {
  const mouse: MouseSpring = {
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    active: false,
    strength: 0,
    lastSpeedScale: 1,
  };
  const ENT = {
    delay: 0.25,
    dur: 2.6,
    stag: 0.34,
    a: 3.2,
    b: 5.6,
    t0: 0,
    done: false,
  };

  function spring(u: number): number {
    if (u <= 0) return 0;
    if (u >= 1) return 1;
    return 1 - (1 - u) * Math.exp(-ENT.a * u) * Math.cos(ENT.b * u);
  }

  function burst(g: Frame, seed: number): number {
    if (ENT.done) return 1;
    const E = (g.t - ENT.t0 - ENT.delay) / ENT.dur;
    if (E >= 1 + ENT.stag) {
      ENT.done = true;
      return 1;
    }
    return spring(E - ENT.stag * g.hash(seed, 11.3));
  }

  /* ------------------------------------------------------------------
  EXIT
  ------------------------------------------------------------------ */
  const EXIT = {
    dur: 0.9,
    hold: 0.05,
    t0: 0,
    arm: false,
    running: false,
    done: false,
  };

  function launch(): void {
    if (EXIT.running || EXIT.arm) return;
    if (opts.reducedMotion) {
      EXIT.done = true;
      opts.onExitComplete();
      return;
    }
    EXIT.arm = true;
  }

  /**
   * Exact mirror of burst(): same spring curve, same per-particle stagger,
   * but time runs backwards. Particles that arrived last (high hash → latest
   * stagger delay on entry) leave first (least delay on exit).
   * u is exit progress 0→1 over ENT.dur seconds.
   */
  function burstReverse(g: Frame, seed: number): number {
    // Run over EXIT.dur instead of ENT.dur so the exit is snappy.
    // Same stagger logic: last to arrive (hash=1) leaves first (delay=0),
    // first to arrive (hash=0) leaves last (delay=stag).
    const E = (g.t - EXIT.t0) / EXIT.dur;
    const localE = E - ENT.stag * (1 - g.hash(seed, 11.3));
    return spring(Math.max(0, Math.min(1, 1 - localE)));
  }

  function exitDone(g: Frame): boolean {
    return (g.t - EXIT.t0) / EXIT.dur >= 1 + ENT.stag;
  }

  function collapseState(g: Frame): void {
    if (!EXIT.running) return;
    const totalDur = EXIT.dur * (1 + ENT.stag);
    const u = Math.max(0, Math.min(1, (g.t - EXIT.t0) / totalDur));
    opts.onExitProgress({ u, collapse: 1 - u });
    if (exitDone(g) && !EXIT.done) {
      EXIT.done = true;
      window.setTimeout(() => opts.onExitComplete(), EXIT.hold * 1000);
    }
  }

  interface FieldCtx {
    g: Frame;
    RH: number;
    HOLE: number;
    proj: Projector;
    F: ReturnType<typeof frontStates>;
    jitOf: (s: number) => number;
    twOf: (s: number) => number;
  }

  interface CollapseCtx {
    dummy?: never;
  }

  function pushDust(
    ctx: FieldCtx,
    pts: Point[],
    _col: CollapseCtx,
    mpos: { x: number; y: number; strength: number } | null,
  ): void {
    const { g, RH, HOLE, proj, F, jitOf, twOf } = ctx;
    const p = g.pal;
    const N = 900;
    for (let i = 0; i < N; i++) {
      const v3 = g.fib(i, N);
      const rest = HOLE + (RH - HOLE) * Math.pow(g.hash(i, 3.1), 0.72);
      const rad = rest * (EXIT.running ? burstReverse(g, i) : burst(g, i));
      const Q = proj(v3[0] * rad, v3[1] * rad, v3[2] * rad);
      const dep = (Q[2] / Math.max(1, rad) + 1) / 2;
      const L = lightAt(
        F,
        (v3[0] * rad) / RH,
        (v3[1] * rad) / RH,
        (v3[2] * rad) / RH,
        jitOf(i),
      );
      const gl = L[0];
      const ed = L[1] * twOf(i);
      let qx = Q[0];
      let qy = Q[1];
      if (mpos) {
        const [nx, ny] = gravNudge(
          qx,
          qy,
          mpos.x,
          mpos.y,
          RH * 0.025,
          RH * 0.2,
          i,
          g,
        );
        qx += nx * mpos.strength;
        qy += ny * mpos.strength;
      }
      pts.push({
        x: qx,
        y: qy,
        z: Q[2],
        r: (0.42 + 0.8 * dep + 0.85 * EDGE_K * ed + 0.22 * GLOW_K * gl) * g.rs,
        c: p.dust,
        a: Math.min(
          0.95,
          (0.05 + 0.09 * dep + 0.2 * GLOW_K * gl + 0.26 * EDGE_K * ed) *
            p.dustA,
        ),
      });
    }
  }

  function pushRings(
    ctx: FieldCtx,
    pts: Point[],
    _col: CollapseCtx,
    mpos: { x: number; y: number; strength: number } | null,
  ): void {
    const { g, RH, proj, F, jitOf, twOf } = ctx;
    const p = g.pal;
    for (let oi = 0; oi < 11; oi++) {
      const w = g.hash(oi, 1.7);
      const T = g.hash(oi, 5.2);
      const f2 = g.hash(oi, 8.9);
      const a =
        RH *
        (0.3 + 0.62 * w) *
        (EXIT.running ? burstReverse(g, 200 + oi) : burst(g, 200 + oi));
      const phi = w * 6.2832;
      const the = Math.acos(2 * T - 1);
      const B = g.basis(
        Math.sin(the) * Math.cos(phi),
        Math.cos(the),
        Math.sin(the) * Math.sin(phi),
      );
      const sp = (0.1 + 0.2 * f2) * (f2 > 0.5 ? 1 : -1);
      for (let q = 0; q < 42; q++) {
        const A = (q / 42) * 6.2832;
        const C = Math.cos(A);
        const S = Math.sin(A);
        const ox = (B[0] * C + B[3] * S) * a;
        const oy = (B[1] * C + B[4] * S) * a;
        const oz = (B[2] * C + B[5] * S) * a;
        const Qo = proj(ox, oy, oz);
        const dpo = (Qo[2] / Math.max(1, a) + 1) / 2;
        const so = oi * 43 + q;
        const Lo = lightAt(F, ox / RH, oy / RH, oz / RH, jitOf(so));
        const eo = Lo[1] * twOf(so);
        let rx = Qo[0];
        let ry = Qo[1];
        if (mpos) {
          const [nx, ny] = gravNudge(
            rx,
            ry,
            mpos.x,
            mpos.y,
            RH * 0.05,
            RH * 0.25,
            so + 5000,
            g,
          );
          rx += nx * mpos.strength;
          ry += ny * mpos.strength;
        }
        pts.push({
          x: rx,
          y: ry,
          z: Qo[2],
          r: (0.62 + 0.7 * EDGE_K * eo + 0.18 * GLOW_K * Lo[0]) * g.rs,
          c: p.mid,
          a: Math.min(
            0.9,
            (0.055 + 0.11 * dpo + 0.17 * GLOW_K * Lo[0] + 0.22 * EDGE_K * eo) *
              p.midA,
          ),
        });
      }
      for (let k2 = 0; k2 < 2; k2++) {
        const A2 = g.t * sp + k2 * 3.1416 + T * 6;
        const C2 = Math.cos(A2);
        const S2 = Math.sin(A2);
        const px2 = (B[0] * C2 + B[3] * S2) * a;
        const py2 = (B[1] * C2 + B[4] * S2) * a;
        const pz2 = (B[2] * C2 + B[5] * S2) * a;
        const Q2 = proj(px2, py2, pz2);
        const dp2 = (Q2[2] / Math.max(1, a) + 1) / 2;
        const L2 = lightAt(
          F,
          px2 / RH,
          py2 / RH,
          pz2 / RH,
          jitOf(oi * 43 + 300 + k2),
        );
        let r2x = Q2[0];
        let r2y = Q2[1];
        if (mpos) {
          const [nx, ny] = gravNudge(
            r2x,
            r2y,
            mpos.x,
            mpos.y,
            RH * 0.1,
            RH * 0.2,
            oi * 43 + 300 + k2 + 9000,
            g,
          );
          r2x += nx * mpos.strength;
          r2y += ny * mpos.strength;
        }
        pts.push({
          x: r2x,
          y: r2y,
          z: Q2[2],
          r: (0.58 + 0.78 * dp2 + 0.36 * EDGE_K * L2[1]) * g.rs,
          c: p.ink,
          a: Math.min(
            0.9,
            (0.13 + 0.21 * dp2 + 0.09 * GLOW_K * L2[0]) * p.midA,
          ),
        });
      }
    }
  }

  function pushFronts(
    ctx: FieldCtx,
    pts: Point[],
    _col: CollapseCtx,
    mpos: { x: number; y: number; strength: number } | null,
  ): void {
    const { g, RH, proj, F, twOf } = ctx;
    const p = g.pal;
    for (let k3 = 0; k3 < F.length; k3++) {
      const eb = EXIT.running ? burstReverse(g, 300 + k3) : burst(g, 300 + k3);
      const fr3 = F[k3];
      const cr = Math.sqrt(Math.max(0, 1 - fr3.p * fr3.p)) * RH * eb;
      if (cr < 6) continue;
      const Bc = g.basis(fr3.n[0], fr3.n[1], fr3.n[2]);
      const ctr = [
        fr3.n[0] * fr3.p * RH * eb,
        fr3.n[1] * fr3.p * RH * eb,
        fr3.n[2] * fr3.p * RH * eb,
      ];
      for (let s3 = 0; s3 < 140; s3++) {
        const A3 = (s3 / 140) * 6.2832;
        const C3 = Math.cos(A3);
        const S3 = Math.sin(A3);
        const s3s = s3 + k3 * 151;
        const rr = cr * (1 + (g.hash(s3s, 2.7) * 2 - 1) * CUT_SCAT * 2.2);
        const nof = (g.hash(s3s, 7.9) * 2 - 1) * CUT_SCAT * RH * eb;
        const Qc = proj(
          ctr[0] + (Bc[0] * C3 + Bc[3] * S3) * rr + fr3.n[0] * nof,
          ctr[1] + (Bc[1] * C3 + Bc[4] * S3) * rr + fr3.n[1] * nof,
          ctr[2] + (Bc[2] * C3 + Bc[5] * S3) * rr + fr3.n[2] * nof,
        );
        let fx = Qc[0];
        let fy = Qc[1];
        if (mpos) {
          const [nx, ny] = gravNudge(
            fx,
            fy,
            mpos.x,
            mpos.y,
            RH * 0.025,
            RH * 0.15,
            s3s + 20000,
            g,
          );
          fx += nx * mpos.strength;
          fy += ny * mpos.strength;
        }
        pts.push({
          x: fx,
          y: fy,
          z: Qc[2] + 1,
          r: 0.62 * g.rs,
          c: p.mid,
          a: Math.min(0.7, 0.085 * twOf(s3s) * p.midA),
          blur: 0,
        });
      }
    }
  }

  /** Advance the mouse spring and return the smoothed attractor with its fade strength. */
  function tickMouse(
    _g: Frame,
    dt: number,
  ): { x: number; y: number; strength: number } | null {
    const raw = opts.getMouse?.();
    const fadeSpeed = 3.5;
    if (!raw) {
      mouse.strength = Math.max(0, mouse.strength - fadeSpeed * dt);
      if (mouse.strength === 0) {
        mouse.active = false;
        return null;
      }
      return {
        x: mouse.x,
        y: mouse.y,
        strength: mouse.strength * mouse.lastSpeedScale,
      };
    }
    if (!mouse.active) {
      // start spring at raw position but with zero strength so it fades in
      mouse.x = raw.x;
      mouse.y = raw.y;
      mouse.vx = 0;
      mouse.vy = 0;
      mouse.active = true;
    }
    mouse.strength = Math.min(1, mouse.strength + fadeSpeed * dt);
    const gap = Math.hypot(raw.x - mouse.x, raw.y - mouse.y);
    const k = 18 + gap * 1.2;
    const damp = 2 * Math.sqrt(k);
    const ex = raw.x - mouse.x;
    const ey = raw.y - mouse.y;
    mouse.vx += (k * ex - damp * mouse.vx) * dt;
    mouse.vy += (k * ey - damp * mouse.vy) * dt;
    mouse.x += mouse.vx * dt;
    mouse.y += mouse.vy * dt;
    const speedScale = 1 / (1 + gap * 0.015);
    mouse.lastSpeedScale = speedScale;
    return { x: mouse.x, y: mouse.y, strength: mouse.strength * speedScale };
  }

  /**
   * Gravitational nudge: pulls a 2-D screen point toward the attractor.
   * Each layer gets its own strength and falloff radius so the disturbance
   * reads as happening at different depths.
   */
  function gravNudge(
    px: number,
    py: number,
    mx: number,
    my: number,
    strength: number,
    radius: number,
    seed: number,
    _g: Frame,
  ): [number, number] {
    const dx = mx - px;
    const dy = my - py;
    const d2 = dx * dx + dy * dy;
    if (d2 < 1) return [0, 0];
    const d = Math.sqrt(d2);
    // soft gravitational falloff: peaks near radius/3, fades at radius
    const norm = d / radius;
    const pull = strength * norm * Math.exp(-norm * norm * 1.8);
    // static per-point weight — varies intensity across particles without temporal phase drift
    const wobble = 0.65 + 0.35 * _g.hash(seed, 13.7);
    return [(dx / d) * pull * wobble, (dy / d) * pull * wobble];
  }

  let prevT = -1;

  const render: RenderFn = (g: Frame): RenderOutput => {
    const pts: Point[] = [];

    if (EXIT.arm) {
      EXIT.arm = false;
      EXIT.running = true;
      EXIT.t0 = g.t;
    }

    const dt = prevT < 0 ? 0.016 : Math.min(0.05, g.t - prevT);
    prevT = g.t;
    const mpos = EXIT.running ? null : tickMouse(g, dt); // strength is 0..1 fade scalar

    collapseState(g);

    /* ---- geometry ----
       RH is half the LONG side so the field runs off top/bottom edges.
       HOLE is the space the search bar stands in. */
    const RH = (Math.max(g.W, g.H) / 2) * 1.06;
    const HOLE = Math.max(96, Math.min(g.W, g.H) * 0.15);
    const proj = g.projector(g.t * 0.041, 0.32, g.cx, g.cy, 1);
    const F = frontStates(g.t);

    const jitOf = (seed: number): number => (g.hash(seed, 9.7) * 2 - 1) * SCAT;
    const twOf = (seed: number): number =>
      1 -
      TWK *
        (0.5 +
          0.5 *
            Math.sin(
              g.t * TWK_HZ * (0.6 + 1.1 * g.hash(seed, 4.3)) +
                g.hash(seed, 6.1) * 6.2832,
            ));

    const ctx: FieldCtx = { g, RH, HOLE, proj, F, jitOf, twOf };
    const col: CollapseCtx = {};
    pushDust(ctx, pts, col, mpos);
    pushRings(ctx, pts, col, mpos);
    pushFronts(ctx, pts, col, mpos);

    return { pts, sats: [] };
  };

  return { render, launch, running: () => EXIT.running };
}
